import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock, Loader2, Package } from "lucide-react";
import { ridesApi, inr, errorMessage } from "../../lib/api";
import { paymentsApi, openCheckout } from "../../lib/razorpay";
import { useAuth } from "../../context/AuthContext";

function useCountdown(iso) {
  const [left, setLeft] = useState(() => (iso ? Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 1000)) : 0));
  useEffect(() => {
    if (!iso) return;
    const tick = () => setLeft(Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [iso]);
  return left;
}

function PendingRow({ ride, onChange }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const left = useCountdown(ride.payment_expires_at);
  const mm = Math.floor(left / 60);
  const ss = String(left % 60).padStart(2, "0");

  const pay = async () => {
    setBusy(true);
    try {
      // Safe retry: confirm an earlier captured payment before charging again.
      const existing = await paymentsApi.recheckRide(ride.ride_id);
      if (existing?.payment_status === "paid") { toast.success("Payment already received! Finding a delivery partner…"); onChange?.(); return; }
      const order = await paymentsApi.createRideOrder(ride.ride_id);
      const res = await openCheckout({ order, amount: ride.fare, user, description: "Goods delivery (prepaid)" });
      await paymentsApi.verifyRide({ ride_id: ride.ride_id, razorpay_order_id: res.razorpay_order_id || order.order_id || `web_${ride.ride_id}`, razorpay_payment_id: res.razorpay_payment_id, razorpay_signature: res.razorpay_signature || null, method: "online" });
      toast.success("Payment received! Finding a delivery partner…");
      onChange?.();
    } catch (e) {
      toast.error(e?.cancelled ? "Payment cancelled" : errorMessage(e, "Payment couldn't be confirmed. Please retry — you won't be charged twice."));
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    setBusy(true);
    try { await ridesApi.cancel(ride.ride_id); toast.success("Booking cancelled"); onChange?.(); }
    catch (e) { toast.error(errorMessage(e, "Couldn't cancel")); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid={`pending-payment-${ride.ride_id}`}>
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-200 text-amber-800"><Package className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-ink">Payment pending · {inr(ride.fare)}</p>
          <p className="truncate text-xs text-ink-soft">{ride.pickup_address} → {ride.drop_address}</p>
          <p className="mt-1 inline-flex items-center gap-1.5 text-xs font-bold text-amber-700" data-testid={`pending-countdown-${ride.ride_id}`}>
            <Clock className="h-3.5 w-3.5" /> {left > 0 ? `Auto-cancels in ${mm}:${ss}` : "Expiring…"}
          </p>
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        <button onClick={pay} disabled={busy} className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full bg-brand px-3 py-2 text-xs font-bold text-white hover:bg-brand-600 disabled:opacity-60" data-testid={`pending-pay-${ride.ride_id}`}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Complete payment</button>
        <button onClick={cancel} disabled={busy} className="rounded-full border border-amber-300 bg-white px-3 py-2 text-xs font-semibold text-ink-soft hover:text-ink disabled:opacity-60" data-testid={`pending-cancel-${ride.ride_id}`}>Cancel</button>
      </div>
    </div>
  );
}

export default function PendingGoodsPayments({ rides, onChange }) {
  const pending = (rides || []).filter((r) => r.status === "awaiting_payment" && r.purpose === "goods");
  if (!pending.length) return null;
  return (
    <div className="mb-5 space-y-2" data-testid="pending-goods-payments">
      {pending.map((r) => <PendingRow key={r.ride_id} ride={r} onChange={onChange} />)}
    </div>
  );
}
